import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { getDnaReview, saveDnaReview, removeDna, restoreDna, removeAllDnaConflicts } from "../../lib/api";
import { useAuth } from "../../App";

const BEHAVIOR_OPTS = [
  { value: "none", label: "N/A" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
];

// Shared Review DNA panel. A DNA stays a non-scored "did not attend" record;
// here staff describe what the student actually did during that block and may
// add a behavior score (which DOES count toward the behavior average).
export default function DnaReviewDialog({ open, onOpenChange, date, onSaved, allowDateChange = false }) {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});   // id -> {dna_activity, behavior}
  const [savingId, setSavingId] = useState(null);
  const [selDate, setSelDate] = useState(date);

  // Keep the selected date in sync with the incoming prop each time the dialog opens.
  useEffect(() => {
    if (open) setSelDate(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, date]);

  const load = async () => {
    if (!selDate) return;
    setLoading(true);
    try {
      const res = await getDnaReview(selDate);
      setData(res.data);
      const d = {};
      (res.data.items || []).forEach((it) => {
        d[it.id] = {
          dna_activity: it.dna_activity || "",
          behavior: it.behavior != null ? String(it.behavior) : "none",
        };
      });
      setDrafts(d);
    } catch {
      toast.error("Failed to load DNA list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && selDate) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selDate]);

  const setDraft = (id, patch) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const handleSave = async (item) => {
    const draft = drafts[item.id] || {};
    setSavingId(item.id);
    try {
      await saveDnaReview(item.id, {
        dna_activity: draft.dna_activity || "",
        behavior: draft.behavior,
      });
      toast.success(`Saved review for ${item.student_name}`);
      await load();
      onSaved && onSaved();
    } catch {
      toast.error("Failed to save review");
    } finally {
      setSavingId(null);
    }
  };

  const handleRemove = async (item, defaultReason = "") => {
    const reason = window.prompt(
      `Remove this DNA for ${item.student_name} (${item.block})?\nEnter a reason (required):`,
      defaultReason,
    );
    if (reason == null) return;
    if (!reason.trim()) { toast.error("A reason is required to remove a DNA"); return; }
    setSavingId(item.id);
    try {
      await removeDna(item.id, reason.trim());
      toast.success("DNA removed");
      await load();
      onSaved && onSaved();
    } catch {
      toast.error("Failed to remove DNA");
    } finally {
      setSavingId(null);
    }
  };

  const handleRemoveAllConflicts = async () => {
    if (!window.confirm("Remove ALL DNAs that conflict with attendance for this date? They are kept in the database (hidden from reports) and logged.")) return;
    setSavingId("__all__");
    try {
      const res = await removeAllDnaConflicts(selDate);
      toast.success(res.data?.message || "Conflicts removed");
      await load();
      onSaved && onSaved();
    } catch {
      toast.error("Failed to remove conflicts");
    } finally {
      setSavingId(null);
    }
  };

  const handleRestore = async (r) => {
    setSavingId(r.id);
    try {
      await restoreDna(r.id);
      toast.success(`Re-entered DNA for ${r.student_name}`);
      await load();
      onSaved && onSaved();
    } catch {
      toast.error("Failed to re-enter DNA");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto" data-testid="dna-review-dialog">
        <DialogHeader>
          <DialogTitle className="text-orange-700">Review DNA — {selDate}</DialogTitle>
          <DialogDescription>
            Did-not-attend blocks (no class or 1:1 was logged). Note what the student did
            and optionally add a behavior score.
          </DialogDescription>
          {allowDateChange && (
            <div className="flex items-center gap-2 pt-2">
              <label className="text-sm font-medium text-slate-600" htmlFor="dna-review-date">Date</label>
              <input
                id="dna-review-date"
                type="date"
                value={selDate || ""}
                onChange={(e) => setSelDate(e.target.value)}
                className="h-9 rounded-md border border-slate-300 px-2 text-sm"
                data-testid="dna-review-date-picker"
              />
            </div>
          )}
        </DialogHeader>

        {loading ? (
          <p className="text-center py-10 text-muted-foreground">Loading…</p>
        ) : !data || (data.items.length === 0 && (data.recently_removed || []).length === 0) ? (
          <p className="text-center py-10 text-green-600" data-testid="dna-review-empty">
            No DNA blocks to review for this date.
          </p>
        ) : (
          <div className="space-y-4">
            {data.items.length === 0 && (
              <p className="text-center py-4 text-green-600 text-sm" data-testid="dna-review-none-active">
                No active DNA blocks to review for this date.
              </p>
            )}
            <div className="flex gap-4 text-sm">
              <span className="px-2 py-1 rounded bg-orange-100 text-orange-700 font-semibold" data-testid="dna-review-needs">
                {data.needs_review} need review
              </span>
              <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-700 font-semibold">
                {data.reviewed} reviewed
              </span>
            </div>

            {(data.conflicts || []).length > 0 && (
              <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-3" data-testid="dna-conflicts-section">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-rose-700" data-testid="dna-conflicts-title">
                    Conflicts with attendance ({data.conflicts.length})
                  </h4>
                  <Button size="sm" variant="outline" className="border-rose-400 text-rose-700 hover:bg-rose-100"
                    disabled={savingId === "__all__"} onClick={handleRemoveAllConflicts}
                    data-testid="dna-remove-all-conflicts">
                    {savingId === "__all__" ? "Removing…" : "Remove all conflicts"}
                  </Button>
                </div>
                <p className="text-xs text-rose-600 mb-2">
                  These students were out of the building (or scheduled elsewhere) for the block — the DNA is likely wrong.
                </p>
                <div className="space-y-2">
                  {data.conflicts.map((c) => (
                    <div key={c.id} className="flex items-center justify-between gap-2 rounded bg-white border border-rose-200 px-3 py-2"
                      data-testid={`dna-conflict-${c.id}`}>
                      <div className="min-w-0">
                        <span className="font-semibold text-slate-900">{c.student_name}</span>
                        <span className="text-sm text-slate-500 ml-2">{c.block}{c.class_name ? ` · ${c.class_name}` : ""}</span>
                        <p className="text-xs text-rose-600">{c.reason}</p>
                      </div>
                      <Button size="sm" variant="outline" className="border-rose-400 text-rose-700 hover:bg-rose-100 shrink-0"
                        disabled={savingId === c.id}
                        onClick={() => handleRemove(c, `Conflicts with attendance: ${c.reason}`)}
                        data-testid={`dna-conflict-remove-${c.id}`}>
                        Remove DNA
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.items.map((item) => {
              const draft = drafts[item.id] || { dna_activity: "", behavior: "none" };
              return (
                <div key={item.id}
                  className={`rounded-lg border p-3 ${item.dna_reviewed ? "border-emerald-200 bg-emerald-50/40" : "border-orange-200 bg-orange-50/40"}`}
                  data-testid={`dna-review-item-${item.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="font-semibold text-slate-900">{item.student_name}</span>
                      <span className="text-sm text-slate-500 ml-2">
                        {item.block}{item.class_name ? ` · ${item.class_name}` : ""}
                      </span>
                    </div>
                    {item.dna_reviewed && (
                      <span className="text-xs font-semibold text-emerald-700" data-testid={`dna-reviewed-badge-${item.id}`}>Reviewed</span>
                    )}
                    {(data.conflict_ids || []).includes(item.id) && (
                      <span className="text-xs font-semibold text-rose-700 flex items-center gap-1" title="Conflicts with attendance" data-testid={`dna-conflict-badge-${item.id}`}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                        Conflict
                      </span>
                    )}
                  </div>
                  <Textarea
                    value={draft.dna_activity}
                    onChange={(e) => setDraft(item.id, { dna_activity: e.target.value })}
                    placeholder="What did the student do during this block (if known)?"
                    rows={2}
                    className="bg-white"
                    data-testid={`dna-activity-input-${item.id}`}
                  />
                  <div className="flex items-end gap-3 mt-2">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Behavior</label>
                      <Select value={draft.behavior} onValueChange={(v) => setDraft(item.id, { behavior: v })}>
                        <SelectTrigger className="w-24 h-9 bg-white" data-testid={`dna-behavior-select-${item.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {BEHAVIOR_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 ml-auto"
                      disabled={savingId === item.id}
                      onClick={() => handleRemove(item)}
                      data-testid={`dna-remove-${item.id}`}>
                      Remove DNA
                    </Button>
                    <Button size="sm" className="bg-[#00305E] hover:bg-[#00305E]/90"
                      disabled={savingId === item.id}
                      onClick={() => handleSave(item)}
                      data-testid={`dna-review-save-${item.id}`}>
                      {savingId === item.id ? "Saving…" : "Save"}
                    </Button>
                  </div>
                </div>
              );
            })}

            {(data.recently_removed || []).length > 0 && (
              <div className="rounded-lg border border-slate-300 bg-slate-50 p-3" data-testid="dna-recently-removed-section">
                <h4 className="font-bold text-slate-700 mb-2" data-testid="dna-recently-removed-title">
                  Recently removed ({data.recently_removed.length})
                </h4>
                <div className="space-y-2">
                  {data.recently_removed.map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-2 rounded bg-white border border-slate-200 px-3 py-2"
                      data-testid={`dna-removed-${r.id}`}>
                      <div className="min-w-0">
                        <span className="font-semibold text-slate-900">{r.student_name}</span>
                        <span className="text-sm text-slate-500 ml-2">{r.block}{r.class_name ? ` · ${r.class_name}` : ""}</span>
                        <p className="text-xs text-slate-500">
                          Removed{r.deleted_by_name ? ` by ${r.deleted_by_name}` : ""}{r.deleted_at ? ` · ${new Date(r.deleted_at).toLocaleString()}` : ""}
                        </p>
                        <p className="text-xs text-slate-600 italic">Reason: {r.deleted_reason || "—"}</p>
                      </div>
                      {isAdmin() && (
                        <Button size="sm" variant="outline" className="shrink-0"
                          disabled={savingId === r.id}
                          onClick={() => handleRestore(r)}
                          data-testid={`dna-reenter-${r.id}`}>
                          {savingId === r.id ? "…" : "Re-enter"}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
